import {
  createGtinValidator,
  createUpc12Validator,
  getRestrictedTextValidationRules,
  getProductDescriptionValidationRules,
} from 'common/components/product-add/components/utils/validation';
import {
  LENGTH_PRODUCT_NAME,
  LENGTH_PRODUCT_DESCRIPTION,
} from 'common/components/product-add/components/constants';
import { sanitizeProduct } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';

// ---------------------------------------------------------------------------
// Field name normalizer
//
// The BE returns `fieldName` in PascalCase (e.g. "UnitUpc", "CaseGtin").
// Client-side code matches against camelCase column field names (e.g. "unitUpc").
// This converts any BE error into the unified shape the grid expects:
//   { field: string (camelCase), message: string, messageType: string }
// ---------------------------------------------------------------------------

/**
 * Converts a PascalCase BE fieldName to camelCase.
 * e.g. "UnitUpc" → "unitUpc", "CaseGtin" → "caseGtin"
 */
const toCamelCaseField = (fieldName) => {
  if (!fieldName) return null;
  return fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
};

/**
 * Normalizes a single BE validation result entry into the unified shape.
 * Handles both `fieldName` (BE) and `field` (client-side) sources.
 */
const normalizeValidationEntry = (entry) => ({
  field: toCamelCaseField(entry.fieldName ?? entry.field ?? null),
  message: entry.message,
  messageType: entry.messageType,
});

export const applyValidationResult = ({ response, products }) => {
  // API call failed (e.g. "Duplicate items are not allowed") —
  // preserve each product's existing status/errors rather than
  // resetting everything to success
  if (!response?.isSuccess) {
    return products;
  }

  const { data } = response;

  return products.map((product) => {
    const validationResult = data?.validationResults?.find(
      (result) => result.uniqueId === product.uniqueId
    );

    if (!validationResult) {
      return {
        ...product,
        validationErrors: null,
        status: 'success',
        canSubmit: true,
      };
    }

    // Normalize all entries — converts PascalCase fieldName → camelCase field
    const normalizedErrors = (validationResult.validationResults ?? []).map(
      normalizeValidationEntry
    );
    const errors = normalizedErrors.filter(
      (error) => !(product.variableWeight === true && error.field === 'unitUpc')
    );

    const errorMessages = errors.filter((err) => err.messageType === 'Error');
    const warningMessages = errors.filter(
      (err) => err.messageType === 'Warning'
    );
    const confirmationMessages = errors.filter(
      (err) => err.messageType === 'WarningWithConfirmation'
    );

    let status = 'success';
    if (errorMessages.length > 0) {
      status = 'error';
    } else if (warningMessages.length > 0 || confirmationMessages.length > 0) {
      // WarningWithConfirmation still needs the row's status icon/tooltip to
      // surface it — it doesn't block canSubmit, but it shouldn't look like
      // a silent 'success' either.
      status = 'warning';
    }

    let sanitizedProduct = sanitizeProduct(product);

    return {
      ...sanitizedProduct,
      validationErrors: errors.length > 0 ? errors : null,
      status,
      canSubmit:
        errors.length !== normalizedErrors.length
          ? errorMessages.length === 0
          : validationResult.passedValidation,
    };
  });
};

/**
 * Collects products flagged with a `WarningWithConfirmation` validation entry
 * (e.g. BE found a Unit UPC match with a different Innerpack/Case configuration
 * and needs the user to confirm it's intentional before submission proceeds).
 */
export const getConfigConfirmationItems = (products) => {
  const items = [];
  for (const product of products) {
    const confirmEntry = (product.validationErrors ?? []).find(
      (e) => e.messageType === 'WarningWithConfirmation'
    );
    if (confirmEntry) {
      items.push({
        uniqueId: product.uniqueId,
        message: confirmEntry.message,
        ...product,
      });
    }
  }
  return items;
};

export const propagateUpcErrorsToShipperItems = (products) => {
  // Build a map of caseGtin → error message for display shipper products.
  // The shipper tab's itemUpc is matched against the parent's caseGtin.
  const caseGtinErrorMap = new Map();
  for (const product of products) {
    const gtinError = (product.validationErrors ?? []).find(
      (e) => e.field === 'caseGtin' && e.messageType === 'Error'
    );
    if (gtinError && product.caseGtin) {
      caseGtinErrorMap.set(product.caseGtin, gtinError.message);
    }
  }

  if (!caseGtinErrorMap.size) return products;

  return products.map((product) => {
    if (!product.displayShipperItems?.length) return product;

    const updatedItems = product.displayShipperItems.map((item) => {
      const errorMessage = caseGtinErrorMap.get(item.itemUpc);

      const otherErrors = (item.validationErrors ?? []).filter(
        (e) => e.field !== 'itemUpc'
      );

      if (!errorMessage) return item;

      return {
        ...item,
        validationErrors: [
          ...otherErrors,
          { field: 'itemUpc', message: errorMessage, messageType: 'Error' },
        ],
      };
    });

    return { ...product, displayShipperItems: updatedItems };
  });
};

const runStrictUpc12Validator = async (label, value) => {
  if (!value) return null;
  if (value.length !== 12) return `${label} must be exactly 12 digits`;
  return runAsyncValidator(createUpc12Validator, label, value);
};

/**
 * Run createGtinValidator / createUpc12Validator (async Ant Design validators)
 * and return an error message string or null.
 */
export const runAsyncValidator = async (validatorFn, fieldLabel, value) => {
  if (!value) return null;
  try {
    await validatorFn(fieldLabel)({}, value);
    return null;
  } catch (err) {
    const raw = err instanceof Error ? err.message : String(err);
    return raw.replace(/^Error:\s*/i, '').trim();
  }
};

/**
 * Run Ant Design Form.Item-style validation rules (pattern or validator)
 * against a value. Returns the first failing rule's message, or null.
 */
const runTextValidationRules = async (rules, value) => {
  for (const rule of rules) {
    if (rule.pattern) {
      if (!rule.pattern.test(value ?? '')) {
        return rule.message;
      }
    } else if (rule.validator) {
      try {
        await rule.validator(null, value);
      } catch (err) {
        return err instanceof Error ? err.message : String(err);
      }
    }
  }
  return null;
};

/**
 * Validate a single wizard product on the client side.
 * Returns the product with updated `status` and `validationErrors` fields.
 *
 * validationErrors shape: Array<{ field: string, message: string, messageType: 'Error'|'Warning' }>
 *   - field is always camelCase, matching AG Grid column field names
 */
export const validateWizardProduct = async (product) => {
  // Reset disabled fields to null before validating so that stale values
  // from previously enabled states don't cause false validation errors.
  product = sanitizeProduct(product);

  const errors = [];

  const isDisplayShipper = Boolean(product?.displayShipper);
  const isFoodService = Boolean(product?.foodServiceBulkItems);
  const isVariableWeight = Boolean(product?.variableWeight);

  const addError = (field, message) =>
    errors.push({ field, message, messageType: 'Error' });

  // ── Required fields ────────────────────────────────────────────────────
  if (!product.productName?.trim()) {
    addError('productName', 'Product Name is required');
  } else if (product.productName.length > LENGTH_PRODUCT_NAME) {
    addError(
      'productName',
      `Product Name must not exceed ${LENGTH_PRODUCT_NAME} characters.`
    );
  } else {
    const msg = await runTextValidationRules(
      getRestrictedTextValidationRules('Product Name'),
      product.productName
    );
    if (msg) addError('productName', msg);
  }

  if (!product.unitUpc?.trim() && !isDisplayShipper && !isVariableWeight) {
    addError('unitUpc', 'Unit UPC12 is required');
  }

  if (!product.productDescription?.trim()) {
    addError('productDescription', 'Product Description is required');
  } else if (product.productDescription.length > LENGTH_PRODUCT_DESCRIPTION) {
    addError(
      'productDescription',
      `Product Description must not exceed ${LENGTH_PRODUCT_DESCRIPTION} characters.`
    );
  } else {
    const msg = await runTextValidationRules(
      getProductDescriptionValidationRules(),
      product.productDescription
    );
    if (msg) addError('productDescription', msg);
  }

  if (!product.brandName?.trim()) {
    addError('brandName', 'Product Brand is required');
  }

  if (
    product.size === null ||
    product.size === undefined ||
    product.size === ''
  ) {
    addError('size', 'Product Size is required');
  }

  if (!product.sizeUom?.trim()) {
    addError('sizeUom', 'Product UOM is required');
  }

  // caseGtin required logic:
  // - Display Shipper: always required (noCodeCase is hidden/irrelevant)
  // - Normal product: required only when noCodeCase is not checked
  if (isDisplayShipper) {
    if (!product.caseGtin?.trim()) {
      addError('caseGtin', 'Case GTIN is required');
    }
  } else if (
    (!product.noCodeCase || isVariableWeight) &&
    !product.caseGtin?.trim() &&
    !product.caseUpc12?.trim()
  ) {
    addError('caseGtin', 'Case GTIN is required');
  }

  // innerPackUpc required when innerPackSellable is true — except Food
  // Service, where innerPackUpc is disabled/cleared regardless
  if (
    product.innerPackSellable &&
    !isFoodService &&
    !product.innerPackUpc?.trim()
  ) {
    addError('innerPackUpc', 'Innerpack UPC is required');
  }

  if (product.unitUpc && !isDisplayShipper) {
    const msg = await runAsyncValidator(
      createUpc12Validator,
      'Unit UPC-12',
      product.unitUpc
    );
    if (msg) addError('unitUpc', msg);
  }

  if (product.caseUpc12) {
    const msg = await runStrictUpc12Validator('Case UPC-12', product.caseUpc12);
    if (msg) addError('caseUpc12', msg);
  }

  if (product.caseGtin) {
    const msg = await runAsyncValidator(
      createGtinValidator,
      'Case GTIN',
      product.caseGtin
    );
    if (msg) addError('caseGtin', msg);
  }

  if (product.innerPackUpc && !isDisplayShipper) {
    const msg = await runStrictUpc12Validator(
      'Innerpack UPC',
      product.innerPackUpc
    );
    if (msg) addError('innerPackUpc', msg);
  }

  if (product.innerPackGtin && !isDisplayShipper) {
    const msg = await runAsyncValidator(
      createGtinValidator,
      'Innerpack GTIN',
      product.innerPackGtin
    );
    if (msg) addError('innerPackGtin', msg);
  }

  const status = errors.length > 0 ? 'error' : 'success';

  return {
    ...product,
    validationErrors: errors.length > 0 ? errors : null,
    status,
    canSubmit: errors.length === 0,
  };
};

export const replaceUndefinedWithNull = (obj) =>
  JSON.parse(JSON.stringify(obj, (k, v) => (v === undefined ? null : v)));
